// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu / Categories
// One catch: real credited photo, the item's own tags, description and per-kg price. The Add button ALWAYS opens the
// pack-size + cut sheet (components/menu/PackSheet) — sizes and cut are chosen there, not here. Once the catch is in the
// cart the card shows what is in it ("1 × 500 g + 1 × 1 kg · Curry Cut · ₹1,275") and the button becomes Edit, which reopens
// the same sheet. Two layouts of the same content: "row" (compact, the menu list) and "tile" (large photo on top, the
// category page, which also lists the cuts the catch is offered in). Data: data/seafood.ts / packs.ts / cuts.ts.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from "react-router-dom";
import { Check, Pencil, Plus, Scissors } from "lucide-react";
import { cutsFor, prepFee, type CutOption } from "../../data/cuts";
import { PACK_SIZES, packPrice, type PackId } from "../../data/packs";
import type { SeafoodItem } from "../../data/seafood";
import { formatInr } from "../../utils/priceFormat";
import FavouriteHeart from "./FavouriteHeart";
import { getWikimediaSrcSet } from "../../utils/wikimediaImage";

interface ProductCardProps {
  item: SeafoodItem;
  /** Packs of this catch in the cart, by size. */
  inCart: Record<PackId, number>;
  /** The cut chosen for this catch in the cart, if it is in the cart. */
  cut?: CutOption;
  /** Opens the pack-size sheet for this catch. */
  onOpen: () => void;
  /** The save-to-favourites heart; omitted for signed-out visitors. */
  favourite?: { saved: boolean; onToggle: () => void };
  variant?: "row" | "tile";
}

export default function ProductCard({ item, inCart, cut, onOpen, favourite, variant = "row" }: ProductCardProps) {
  const packs = PACK_SIZES.filter((pack) => inCart[pack.id] > 0);
  const cartTotal = packs.reduce((sum, pack) => sum + inCart[pack.id] * (packPrice(item, pack) + (cut ? prepFee(cut, pack) : 0)), 0);
  const summary = packs.map((pack) => `${inCart[pack.id]} × ${pack.label}`).join(" + ");
  const tile = variant === "tile";
  const heart = favourite ? <FavouriteHeart name={item.name} saved={favourite.saved} onToggle={favourite.onToggle} className="absolute right-2 top-2" /> : null;

  const tags =
    item.tags.length > 0 ? (
      <ul aria-label="Tags" className={`flex flex-wrap gap-1 ${tile ? "absolute left-3 top-3" : ""}`}>
        {item.tags.map((tag) => (
          <li key={tag} className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${tile ? "bg-white/95 text-ocean-900 shadow-sm" : "bg-teal-100 text-teal-700"}`}>
            {tag}
          </li>
        ))}
      </ul>
    ) : null;

  const action = (
    <button
      type="button"
      onClick={onOpen}
      aria-label={packs.length > 0 ? `Edit ${item.name} in your cart` : `Add ${item.name} to cart`}
      className={`inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full px-5 text-sm font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
        packs.length > 0 ? "bg-ocean-700/10 text-ocean-900 hover:bg-ocean-700/15 dark:bg-white/10 dark:text-foam-50" : "bg-ocean-800 text-white hover:bg-ocean-700"
      }`}
    >
      {packs.length > 0 ? <Pencil className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
      {packs.length > 0 ? "Edit" : "Add"}
    </button>
  );

  const price = (
    <p>
      <span className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{item.price}</span>
      <span className="ml-1 text-xs text-ocean-800/70 dark:text-foam-100/65">{item.unit}</span>
    </p>
  );

  const inCartLine =
    packs.length > 0 ? (
      <p className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-teal-700 dark:text-teal-300">
        <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        In cart: {summary}{cut ? ` · ${cut.name}` : ""} · {formatInr(cartTotal)}
      </p>
    ) : null;

  if (tile) {
    return (
      <li className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <div className="relative">
          <Link to={`/menu/${item.id}`} tabIndex={-1} aria-hidden="true">
          <img
            src={item.image}
            srcSet={getWikimediaSrcSet(item.image)}
            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            alt={item.name}
            title={item.credit}
            loading="lazy"
            className="h-48 w-full object-cover"
          />
          </Link>
          {tags}
          {heart}
        </div>
        <div className="flex flex-1 flex-col p-4">
          <h3 className="font-display text-lg font-bold leading-snug text-ocean-950 dark:text-foam-50">
            <Link to={`/menu/${item.id}`} className="hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">{item.name}</Link>
          </h3>
          <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">{item.description}</p>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-ocean-800/80 dark:text-foam-100/75">
            <Scissors className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              <span className="sr-only">Cuts offered: </span>
              {cutsFor(item).map((option) => option.name).join(" · ")}
            </span>
          </p>
          <div className="mt-auto flex items-end justify-between gap-2 pt-3">
            {price}
            {action}
          </div>
          {inCartLine}
        </div>
      </li>
    );
  }

  return (
    <li className="relative flex gap-4 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
      <Link to={`/menu/${item.id}`} tabIndex={-1} aria-hidden="true" className="shrink-0">
      <img
        src={item.image}
        srcSet={getWikimediaSrcSet(item.image)}
        sizes="112px"
        alt={item.name}
        title={item.credit}
        loading="lazy"
        width={112}
        height={112}
        className="h-28 w-28 shrink-0 rounded-xl object-cover"
      />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col">
        {tags}
        <h3 className={`mt-1 font-display text-base font-bold leading-snug text-ocean-950 dark:text-foam-50 ${heart ? "pr-11" : ""}`}>
          <Link to={`/menu/${item.id}`} className="hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">{item.name}</Link>
        </h3>
        <p className="mt-0.5 line-clamp-2 text-sm text-ocean-800/80 dark:text-foam-100/75">{item.description}</p>
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          {price}
          {action}
        </div>
        {inCartLine}
      </div>
      {heart}
    </li>
  );
}
